import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "semantic-ui-react";

import { BarcodeFormat } from "../../../utils/helpers/validation";
import { detectBarcodeType } from "../../../utils/helpers/validation";
import { addMessage, MessageType } from "../../../lib/store/reducers/responseMessagesReducer";
import { AppDispatch } from "../../../lib/store/storeConfig";
import AsyncModalForm, { TextField } from "../AsyncModalForm/AsyncModalForm";

type BarcodeButtonProps = {
  title: string;
  textFieldLabel: string;
  disabled?: boolean;
  onBarcodeScanAsync: (scannedValue: string, format: BarcodeFormat) => Promise<void>;
};

const BarcodeModalButton: React.FC<BarcodeButtonProps> = ({
  title,
  textFieldLabel,
  disabled = false,
  onBarcodeScanAsync,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isOpen, setIsOpen] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setBarcode("");
    setIsSubmitting(false);
    setIsOpen(false);
  };

  const handleSubmit = useCallback(async () => {
    const trimmed = barcode.trim();
    if (!trimmed || isSubmitting) return;

    try {
      setIsSubmitting(true);

      const format = detectBarcodeType(trimmed);
      await onBarcodeScanAsync(trimmed, format);
    } catch (error: any) {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: error?.message || "Ett okänt fel inträffade.",
        })
      );
    } finally {
      reset();
    }
  }, [barcode, isSubmitting, dispatch, onBarcodeScanAsync]);

  return (
    <>
      <Button
        color="yellow"
        fluid
        size="big"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        content={title}
        style={{ gridColumn: "1 / -1" }}
      />

      <AsyncModalForm
        title={title}
        isOpen={isOpen}
        onClose={reset}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitDisabled={!barcode.trim()}
      >
        <TextField
          label={textFieldLabel}
          value={barcode}
          placeholder="Skanna eller skriv in..."
          disabled={isSubmitting}
          onChange={setBarcode}
          onEnter={handleSubmit}
        />
      </AsyncModalForm>
    </>
  );
};

export default BarcodeModalButton;
